/**
 * /pipeline-facility/digs — the dig register (GET /pipeline-facility/digs,
 * fieldiq-api#1219, PF-3a).
 *
 * A dig is made from its job (job → Digs → New dig), so this register has no
 * New button of its own; it says where digs come from instead. Filters (status,
 * site, search) live in the URL. A field hand sees the API's list — digs on
 * jobs they are crewed on.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Search, Shovel } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfMeta, usePfSites } from '../../../hooks/usePipelineFacility';
import { usePfDigs } from '../../../hooks/usePfDigs';
import { queryLoadFailure } from '../../../lib/load-failure';
import { fmtNumber, pfDay, withUnit } from '../../../lib/pipeline-facility';
import { pfDigStatusSkin } from '../../../lib/pf-digs';
import type { PfDig } from '../../../types/pf-digs';
import { useOpenRecord } from '../../downhole/recordPage';
import { PF_EYEBROW, PF_INPUT, PfBadge, PfChip, PfChipRow, PfEmpty, PfPanel, PfRegister, type PfColumn } from '../PfBits';

export function oneCallText(d: { oneCallTicket: string | null; oneCallValidTo: string | null; oneCallExpired: boolean | null }) {
  if (!d.oneCallTicket && !d.oneCallValidTo) return '—';
  return `${d.oneCallTicket || '—'}${d.oneCallValidTo ? ` · valid to ${pfDay(d.oneCallValidTo)}` : ''}${d.oneCallExpired ? ' (expired)' : ''}`;
}

export default function PfDigsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const siteId = searchParams.get('siteId') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(qParam);
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== qParam) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const catalog = usePfMeta().data?.data ?? null;
  const sites = usePfSites().data?.data ?? [];
  const filters = useMemo(() => ({ status, siteId, q: qParam }), [status, siteId, qParam]);
  const filtered = !!(status || siteId || qParam);
  const listQuery = usePfDigs(filters);
  const allQuery = usePfDigs();
  const digs = listQuery.data?.data ?? [];
  const units = listQuery.data?.meta.units ?? null;
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (d: PfDig) => boolean) => displayValue(all ? all.filter(pick).length : undefined);

  const columns: PfColumn<PfDig>[] = [
    {
      key: 'dig',
      header: 'Dig',
      render: (d) => (
        <>
          <p className="font-mono font-extrabold text-white">{d.digNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{d.jobNumber || '—'}</p>
        </>
      ),
    },
    { key: 'site', header: 'Site', render: (d) => (<><p>{d.siteName || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{d.segmentLabel || '—'}</p></>) },
    { key: 'chainage', header: `Chainage${units?.chainage ? ` (${units.chainage})` : ''}`, align: 'right', render: (d) => <span className="font-mono">{fmtNumber(d.chainage)}</span> },
    { key: 'onecall', header: 'One-call / GD ticket', render: (d) => <span className={d.oneCallExpired ? 'font-bold text-fieldiq-text' : ''}>{oneCallText(d)}</span> },
    { key: 'features', header: 'Features', align: 'right', render: (d) => displayValue(d.featureCount ?? undefined) },
    { key: 'repairs', header: 'Repairs', align: 'right', render: (d) => displayValue(d.repairCount ?? undefined) },
    { key: 'excavated', header: 'Excavated', render: (d) => pfDay(d.excavatedOn) },
    { key: 'report', header: 'Report', render: (d) => <span className="font-mono">{d.reportNumber || (d.reportStatus === 'draft' ? 'Draft' : '—')}</span> },
    { key: 'status', header: 'Status', render: (d) => <PfBadge skin={pfDigStatusSkin(d.status)} value={d.status} /> },
  ];

  return (
    <div data-testid="pf-digs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="Digs"
        description="Integrity digs — excavation, features found, repairs, recoat, backfill and the dig report."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Shovel className="size-4" aria-hidden="true" />, label: 'Digs', tone: 'neutral', loading: allQuery.isLoading, value: countOf(() => true) },
          { icon: <Shovel className="size-4" aria-hidden="true" />, label: 'Open', tone: 'accent', loading: allQuery.isLoading, value: countOf((d) => d.status !== 'reported') },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Ticket expired', tone: 'neutral', loading: allQuery.isLoading, value: countOf((d) => !!d.oneCallExpired && d.status !== 'reported') },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            A dig is planned from its job: open the <Link className="underline" to="/pipeline-facility/jobs">job</Link>, then Digs → New dig.
          </p>
          <PfChipRow label="Filter by status">
            <PfChip label="All" active={!status} onClick={() => setParam('status', '')} count={allQuery.isLoading ? undefined : countOf(() => true)} />
            {(catalog?.digStatuses ?? []).map((s) => (
              <PfChip key={s.value} label={s.label} active={status === s.value} onClick={() => setParam('status', s.value)} count={allQuery.isLoading ? undefined : countOf((d) => d.status === s.value)} />
            ))}
          </PfChipRow>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_16rem]">
            <label className="relative block">
              <span className="sr-only">Search dig, job, site or ticket</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search dig, job, site or ticket"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select className={PF_INPUT} aria-label="Filter by site" value={siteId} onChange={(e) => setParam('siteId', e.target.value)}>
              <option value="">All sites</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <PfPanel eyebrow="Dig register" title="Integrity digs" testId="pf-dig-register" footer={!listQuery.isLoading && digs.length ? `${digs.length} dig${digs.length === 1 ? '' : 's'}` : undefined}>
            <PfRegister
              query={listQuery}
              rows={digs}
              columns={columns}
              rail={(d) => pfDigStatusSkin(d.status).rail}
              onOpen={(d) => openRecord(`/pipeline-facility/digs/${d.id}`)}
              subject="digs"
              testId="pf-dig"
              minWidth="min-w-[1000px]"
              empty={filtered ? <PfEmpty>No dig matches these filters.</PfEmpty> : <PfEmpty>No digs yet. Plan one from a job.</PfEmpty>}
              card={(d) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{d.digNumber || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{d.jobNumber || '—'} · {d.siteName || '—'}</p>
                    </div>
                    <PfBadge skin={pfDigStatusSkin(d.status)} value={d.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    Chainage {withUnit(d.chainage, units?.chainage)} · Features {displayValue(d.featureCount ?? undefined)} · {oneCallText(d)}
                  </p>
                </>
              )}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}
